import React, { useState, useEffect } from 'react'
import Uppy from '@uppy/core'
import { DragDrop } from '@uppy/react'
import { css } from '@emotion/css'
import tw from 'twin.macro'
import styled from '@emotion/styled'
import qs from 'qs'
import { getAppIdFromURL } from '@vt/utils'
import { useDitto } from '@ow/ditto-core'
import { imgAudioVideo } from '../../utils/file-constants'
// import { givrali } from '@vt/apis/lib/files'
import '@uppy/core/dist/style.css'
import '@uppy/progress-bar/dist/style.css'
import downloadImg from '../../assets/media/download.png'
import FileItem from './FileItem'

const Item = styled.div`
  &:hover .delete-icon {
    visibility: visible !important;
  }
`

const uppyStyle = css`
  .uppy-DragDrop-container {
    background-color: var(--ow-color-primary-700);
    border-radius: 4px;
    &:focus {
      outline: none;
      box-shadow: 0 0 0 0 rgba($blue, 0.4);
    }
  }
  .uppy-DragDrop--isDragDropSupported {
    border: 0;
  }
  .uppy-DragDrop-note {
    ${tw`text-white flex justify-center items-center`}
    font-size:0.75rem
  }
  .uppy-DragDrop-inner {
    padding: 0;
  }
  .uppy-c-icon {
    display: none;
  }
  .uppy-StatusBar:before {
    height: 0;
  }
`

// value参数类型： [{filename,src}...]
const Attachment = ({ opts = {}, value, onChange }) => {
  const {
    slug,
    serverAuth,
    allowedTypes = true,
    maxSize,
    uploadQuantities,
    limitedFileQuantity,
    allowedWebImport,
    templates = [],
  } = opts
  const { upload: onClickUpload } = useDitto()
  const [selectedfiles, setSelectedfiles] = useState(value || [])
  const [uploadStatus, setUploadStatus] = useState(null)

  // uppy所允许的上传类型（需判断图音频是否允许导入）
  const alloweType = allowedWebImport
    ? allowedTypes
    : allowedTypes.filter((v) => imgAudioVideo.indexOf(v) === -1)

  const uppy = new Uppy({
    id: slug,
    restrictions: {
      maxNumberOfFiles: uploadQuantities || null,
      minNumberOfFiles: 1,
      allowedFileTypes: alloweType,
    },
    debug: true,
  })

  const changeData = (files) => {
    setSelectedfiles(files)
    const links = files.map((v) => {
      return { ...v, serverAuth }
    })

    onChange(links.length ? links : null)
  }

  const upload = (file) => {
    const { extension, name } = file
    const formData = new FormData()
    formData.append('files', file.data)
    formData.append('appId', getAppIdFromURL() || '1')
    if (name) {
      const lastPoint = name.lastIndexOf('.')
      const showName = name.substring(0, lastPoint)
      formData.append('customFileNames', [showName])
    }

    if (onClickUpload) {
      onClickUpload(formData)
        .then(({ data }) => {
          const obj = data[0]

          const [llink, queries] = obj.llink.split('?')

          const currentFile = {
            filename:
              obj.extendedData && obj.extendedData.originalName
                ? obj.extendedData.originalName
                : '',
            name: obj.name,
            src: llink,
            urlSuffixMap: queries && qs.parse(queries),
            fileType: extension,
            time: Date.now(),
          }

          // const currentFile = {
          //   filename: obj.extendedData.originalName,
          //   src: obj.llink,
          //   extension,
          // }
          setUploadStatus('success')
          changeData([currentFile].concat(selectedfiles))
        })
        .catch(() => {
          setUploadStatus('error')
        })
    }
  }

  uppy.on('file-added', (file) => {
    console.log(file, 'file')
    if (file.size > maxSize * 1024 * 1024) {
      setUploadStatus('size-over')
    } else if (
      limitedFileQuantity &&
      selectedfiles.length === uploadQuantities
    ) {
      setUploadStatus('limit')
    } else {
      upload(file)
      setUploadStatus('loading')
    }
  })

  const deleteFile = (file, index) => {
    uppy.removeFile(file.id)
    const newfileInfo = [...selectedfiles]
    newfileInfo.splice(index, 1)
    changeData(newfileInfo)
    setUploadStatus('deleted')
  }

  // 上传按钮text
  const renderBtnText = () => {
    if (uploadQuantities && limitedFileQuantity && !maxSize) {
      return `上传附件(上限${uploadQuantities}个，每个${maxSize}M以内)`
    }

    if (uploadQuantities && limitedFileQuantity && maxSize) {
      return `上传附件(上限${uploadQuantities}个，每个${maxSize}M以内)`
    }

    return maxSize ? `上传附件(${maxSize}M以内)` : '上传附件'
  }

  const renderStatus = () => {
    if (uploadStatus === 'loading') {
      return <span className="text-orange-3">上传中...</span>
    }
    if (uploadStatus === 'success') {
      return <span className="text-blue-1">上传成功!</span>
    }
    if (uploadStatus === 'error') {
      return <span className="text-red-2">上传失败!</span>
    }
    if (uploadStatus === 'deleted') {
      return <span className="text-orange-3">删除成功!</span>
    }
    if (uploadStatus === 'size-over') {
      return <span className="text-red-2">文件大小超出限制</span>
    }
    if (uploadStatus === 'limit') {
      return (
        <span className="text-red-2">不能超过{uploadQuantities}个文件</span>
      )
    }
    return ''
  }

  useEffect(() => {
    if (
      uploadStatus === 'limit' ||
      uploadStatus === 'error' ||
      uploadStatus === 'deleted'
    ) {
      setTimeout(() => {
        setUploadStatus(null)
      }, 3000)
    }
  }, [uploadStatus])

  return (
    <div className="w-full mt-2">
      {(selectedfiles || []).map((file, index) => {
        return (
          <FileItem
            files={selectedfiles.map((v) => {
              return { filename: v.filename, src: v.src }
            })}
            file={file}
            index={index}
            key={file.src}
            deleteFile={deleteFile}
          />
        )
      })}
      {!templates || templates.length === 0 ? (
        <div className="w-full h-full flex flex-col relative cursor-pointer">
          <div className={uppyStyle}>
            <DragDrop
              id={`${slug}-dragDrop`}
              note={renderBtnText()}
              className="z-20"
              uppy={uppy}
              locale={{
                strings: {
                  dropHereOr: '',
                },
              }}
              height={36}
              width="250px"
            />
          </div>
          <div style={{ marginTop: '.375rem', fontSize: '.75rem' }}>
            {renderStatus()}
          </div>
        </div>
      ) : (
        <div className="w-full flex flex-col mt-4">
          {(templates || []).map((item, index) => {
            return (
              <Item
                className="relative flex items-center w-full bg-gray-100 z-10 h-9 leading-9 px-3 mb-2 box-border  dark:border-darkTheme-800 dark:bg-darkTheme-1100 dark:border dark:border-solid"
                style={{ borderRadius: '.25rem', fontSize: '.75rem' }}
              >
                <div className="w-full flex items-center truncate">
                  <span className="w-full truncate">
                    {item.url && (
                      <span
                        className="w-full truncate"
                        style={{ color: 'red' }}
                      >
                        *
                      </span>
                    )}
                    {item.name}
                  </span>
                </div>
                {item.url ? (
                  <a
                    className="w-4 h-4 -ml-3"
                    href={`${item.url}?response-content-type=application/octet-stream`}
                  >
                    <img src={downloadImg} className="w-full h-full" alt="" />
                  </a>
                ) : (
                  <div className="w-4 h-4 -ml-3" />
                )}
                <div className={uppyStyle} style={{ marginLeft: '16px' }}>
                  <DragDrop
                    id={`${slug}-${index}-dragDrop`}
                    note={renderBtnText()}
                    className="z-20"
                    uppy={uppy}
                    locale={{
                      strings: {
                        dropHereOr: '',
                      },
                    }}
                    height={36}
                    width="100px"
                  />
                </div>
              </Item>
            )
          })}
        </div>
      )}
    </div>
  )
}

export default Attachment
